import { useState } from "react";
import { Controller, useController } from "react-hook-form";
import {
  BUTTON_SELECT,
  FIELD_REQUIRED,
  FORM,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getReactHookFormNestedError } from "../../utils/errors";
import { FileText } from "lucide-react";
import {
  BORDER_GRAY_COLOR,
  GRAY_200,
  GRAY_50,
  GRAY_500,
  PAPER_CONTRAST_TEXT,
} from "../../utils/colors";
import _ from "lodash";
import Button from "../Buttons/Button";
import ModalInstantFormsList from "./ModalInstantFormsList";
import { BORDER_RADIUS } from "../../utils/constants";

const InstantFormContainer = ({
  name,
  integrationID,
  pageID,
  control,
  errors = {},
  disabled,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    control,
    name,
    defaultValue: "",
  });
  const [modalTemplateOpen, setModalTemplateOpen] = useState(false);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();

  const onToggleModalTemplate = () => {
    if (disabled) return;
    setModalTemplateOpen((prev) => !prev);
  };

  const isEmptyTemplate = _.isEmpty(value);
  return (
    <Controller
      name={name}
      id={name}
      control={control}
      rules={{
        required: {
          value: true,
          message: t(FIELD_REQUIRED),
        },
      }}
      render={() => (
        <Box id={name}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {!isEmptyTemplate && (
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    bgcolor: GRAY_50,
                    borderRadius: BORDER_RADIUS,
                    border: `1px solid ${BORDER_GRAY_COLOR}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: GRAY_200,
                    }}
                  >
                    <FileText size={16} style={{ flexShrink: 0 }} />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: GRAY_500,
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {t(FORM)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: PAPER_CONTRAST_TEXT,
                      }}
                    >
                      {value.name}
                    </Typography>
                  </Box>
                </Box>

                {!disabled && (
                  <Button
                    type="button"
                    color="secondary"
                    onClick={onToggleModalTemplate}
                    disabled={disabled}
                    sx={{
                      mt: 1.5,
                      width: "100%",
                    }}
                  >
                    {t(BUTTON_SELECT)}
                  </Button>
                )}
              </Box>
            )}
            {isEmptyTemplate && (
              <Button
                type="button"
                color="secondary"
                onClick={onToggleModalTemplate}
                disabled={disabled}
                sx={{
                  mt: 1.5,
                  width: "100%",
                }}
              >
                {t(BUTTON_SELECT)}
              </Button>
            )}
          </Box>
          {error?.message && (
            <Typography variant="body2" color="error">
              {error.message}
            </Typography>
          )}
          {modalTemplateOpen && (
            <ModalInstantFormsList
              modalOpen={modalTemplateOpen}
              onCloseModal={onToggleModalTemplate}
              integrationID={integrationID}
              pageID={pageID}
              onCallbackSelect={(data) => {
                onChange(data);
              }}
            />
          )}
        </Box>
      )}
    />
  );
};

export default InstantFormContainer;
